import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner, faBoxes, faGasPump } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import systemDataService from '../services/systemDataService';
import { CURRENCY_OPTIONS } from '../utils/constants';
import { useFlashMessage } from './FlashMessage';
import AmountInput from './ui/AmountInput';
import {
    MIXED_FUELS_FIELDS,
    EMPTY_MIXED_FUELS_SPEC,
    isMixedFuelsSubtype,
    getMixedFuelsShorthand,
} from '../data/mixedFuelsSpec';

const SHORTNAME_MAX_LENGTH = 64;

// pendingStockItem: the not-yet-written product payload handed over by
// StockItemForm in deferred mode. When set, submitting this form creates the
// PRODUCT AND ITS FIRST ITEM in one atomic POST, so abandoning here leaves
// nothing behind — a product with no item is invisible to every picker
// (invoice-product-search-silent-drops, 2026-08-19).
const StockItemVariantForm = ({ isOpen, onClose, onSuccess, stockItemId, editingVariant, preFilledData, analysisId, parentSubtype, pendingStockItem = null }) => {
    const { showSuccess, showError } = useFlashMessage();
    const [units, setUnits] = useState([]);
    const [formData, setFormData] = useState({
        shortname: '',
        quality: preFilledData?.quality_specifications || '',
        quantity: preFilledData?.quantity || '',
        unit: preFilledData?.unit || null,
        size: preFilledData?.size_specifications || '',
        price_currency: preFilledData?.price_currency || 'USD',
        purchase_price: preFilledData?.purchase_price || '',
        sale_price: preFilledData?.sale_price || '',
        tolerance_lower_value: '',
        tolerance_lower_unit: '%',
        tolerance_upper_value: '',
        tolerance_upper_unit: '%',
        spec_attributes: { ...EMPTY_MIXED_FUELS_SPEC },
        source_type: preFilledData ? 'contract' : 'manual'
    });

    const showMixedFuelsSpec = isMixedFuelsSubtype(parentSubtype);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Track whether the user has manually edited shortname so we don't clobber
    // their input with the auto-generated mixed-fuels shorthand.
    const [shortnameTouched, setShortnameTouched] = useState(false);

    // Fetch units on mount
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const unitsData = await systemDataService.getUnits();
                const activeUnits = unitsData.filter(unit => unit.is_active);
                setUnits(activeUnits);
                
                // Set default unit if not already set
                if (!formData.unit && activeUnits.length > 0 && !editingVariant && !preFilledData) {
                    // Default to MT if available, otherwise first unit
                    const defaultUnit = activeUnits.find(u => u.code === 'MT') || activeUnits[0];
                    setFormData(prev => ({...prev, unit: defaultUnit.id}));
                }
            } catch (error) {
                console.error('Error fetching units:', error);
                showError('Failed to load units');
            }
        };
        fetchUnits();
    }, []);

    // Populate form data when editing or using pre-filled data
    useEffect(() => {
        if (editingVariant) {
            // Existing variant already has a shortname; treat it as user-owned so
            // we don't overwrite it when the spec dropdowns change.
            setShortnameTouched(!!editingVariant.shortname);
            setFormData({
                shortname: editingVariant.shortname || '',
                quality: editingVariant.quality || '',
                quantity: editingVariant.quantity || '',
                unit: editingVariant.unit || null,
                size: editingVariant.size || '',
                price_currency: editingVariant.price_currency || 'USD',
                purchase_price: editingVariant.purchase_price || '',
                sale_price: editingVariant.sale_price || '',
                tolerance_lower_value: editingVariant.tolerance_lower_value || '',
                tolerance_lower_unit: editingVariant.tolerance_lower_unit || '%',
                tolerance_upper_value: editingVariant.tolerance_upper_value || '',
                tolerance_upper_unit: editingVariant.tolerance_upper_unit || '%',
                spec_attributes: {
                    ...EMPTY_MIXED_FUELS_SPEC,
                    ...(editingVariant.spec_attributes || {}),
                },
                source_type: editingVariant.source_type || 'manual'
            });
        } else if (preFilledData) {
            // If preFilledData has unit code, map it to unit ID
            let unitId = null;
            if (preFilledData.unit && units.length > 0) {
                const matchedUnit = units.find(u => u.code === preFilledData.unit || u.code === preFilledData.unit.toUpperCase());
                unitId = matchedUnit ? matchedUnit.id : (units.find(u => u.code === 'MT')?.id || units[0]?.id);
            } else if (units.length > 0) {
                // Default to MT if available
                unitId = units.find(u => u.code === 'MT')?.id || units[0]?.id;
            }

            // If the AI extracted a shortname, lock it in (treat as user-owned)
            // so the spec-derived shorthand auto-fill effect doesn't clobber it.
            // For mixed_fuels we deliberately leave shortnameTouched=false: the
            // contract usually doesn't carry a shortname, and we want the
            // spec-derived shorthand to populate the field automatically.
            const aiShortname = preFilledData.shortname || '';
            setShortnameTouched(!!aiShortname);
            setFormData({
                shortname: aiShortname,
                quality: preFilledData.quality_specifications || '',
                quantity: preFilledData.quantity || '',
                unit: unitId,
                size: preFilledData.size_specifications || '',
                price_currency: preFilledData.price_currency || 'USD',
                purchase_price: preFilledData.purchase_price || '',
                sale_price: preFilledData.sale_price || '',
                tolerance_lower_value: preFilledData.tolerance_lower_value || '',
                tolerance_lower_unit: preFilledData.tolerance_lower_unit || '%',
                tolerance_upper_value: preFilledData.tolerance_upper_value || '',
                tolerance_upper_unit: preFilledData.tolerance_upper_unit || '%',
                // Map the four mixed_fuels_* AI extraction fields onto the JSON
                // shape the variant model stores. Only relevant when the parent
                // stock item is mixed_fuels - otherwise spec_attributes stays
                // empty and the spec section of the form is hidden.
                spec_attributes: isMixedFuelsSubtype(parentSubtype) ? {
                    fuel_category: preFilledData.mixed_fuels_fuel_category || '',
                    iso_grade: preFilledData.mixed_fuels_iso_grade || '',
                    sulfur_limit: preFilledData.mixed_fuels_sulfur_limit || '',
                    iso_year: preFilledData.mixed_fuels_iso_year || '',
                } : { ...EMPTY_MIXED_FUELS_SPEC },
                source_type: 'contract'
            });
        } else if (units.length > 0 && !formData.unit) {
            // Set default unit for new entry
            const defaultUnit = units.find(u => u.code === 'MT') || units[0];
            setFormData(prev => ({
                ...prev,
                shortname: '',
                quality: '',
                quantity: '',
                unit: defaultUnit.id,
                size: '',
                price_currency: 'USD',
                purchase_price: '',
                sale_price: '',
                tolerance_lower_value: '',
                tolerance_lower_unit: '%',
                tolerance_upper_value: '',
                tolerance_upper_unit: '%',
                spec_attributes: { ...EMPTY_MIXED_FUELS_SPEC },
                source_type: 'manual'
            }));
        }
        setErrors({});
    }, [editingVariant, preFilledData, units]);

    // Compute the live shorthand for the current spec so we can mirror it into
    // the shortname field (when untouched) and display it as a preview.
    const liveShorthand = showMixedFuelsSpec ? getMixedFuelsShorthand(formData.spec_attributes) : '';

    // Auto-fill shortname from the live shorthand whenever the spec changes,
    // unless the user has manually typed a shortname.
    useEffect(() => {
        if (!showMixedFuelsSpec || shortnameTouched) return;
        if (!liveShorthand) return;
        if (formData.shortname === liveShorthand) return;
        setFormData(prev => ({ ...prev, shortname: liveShorthand.slice(0, SHORTNAME_MAX_LENGTH) }));
    }, [liveShorthand, showMixedFuelsSpec, shortnameTouched]);

    // Helper function to get unit label for dynamic display
    const getUnitLabel = () => {
        if (formData.unit && units.length > 0) {
            const selectedUnit = units.find(u => u.id === formData.unit);
            return selectedUnit ? selectedUnit.name : 'Unit';
        }
        return 'Unit';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Once the user types in shortname, stop auto-filling it from the spec.
        if (name === 'shortname') {
            setShortnameTouched(true);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Amount fields (quantity / purchase_price / sale_price) render through
    // <AmountInput> (components/ui/AmountInput.js), which emits a canonical
    // dot-decimal string per keystroke. Mirror handleChange's field-patch +
    // error-clear so state and validation stay in lock-step.
    const setAmountField = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Shortname is required
        if (!formData.shortname || !formData.shortname.trim()) {
            newErrors.shortname = 'Shortname is required';
        } else if (formData.shortname.length > SHORTNAME_MAX_LENGTH) {
            newErrors.shortname = `Shortname must be ${SHORTNAME_MAX_LENGTH} characters or less`;
        }
        
        if (formData.quantity && formData.quantity.toString().trim()) {
            if (isNaN(formData.quantity) || parseFloat(formData.quantity) <= 0) {
                newErrors.quantity = 'Quantity must be a positive number';
            }
        }
        
        // Validate other numeric fields if provided
        if (formData.purchase_price && (isNaN(formData.purchase_price) || parseFloat(formData.purchase_price) <= 0)) {
            newErrors.purchase_price = 'Purchase price must be a positive number';
        }
        
        if (formData.sale_price && (isNaN(formData.sale_price) || parseFloat(formData.sale_price) <= 0)) {
            newErrors.sale_price = 'Sale price must be a positive number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        try {
            // Prepare data for submission
            const submitData = {
                ...formData
            };

            // Only persist spec_attributes for subtypes that use it; otherwise null it out so we
            // don't carry stale spec data when the parent product type is something else.
            if (showMixedFuelsSpec) {
                const cleanedSpec = {};
                Object.entries(submitData.spec_attributes || {}).forEach(([key, val]) => {
                    if (val) cleanedSpec[key] = val;
                });
                submitData.spec_attributes = Object.keys(cleanedSpec).length ? cleanedSpec : null;
            } else {
                submitData.spec_attributes = null;
            }

            // Add base_stock_item only for create operations against an
            // EXISTING product. In pending mode the product doesn't exist yet.
            if (!editingVariant && !pendingStockItem) {
                submitData.base_stock_item = stockItemId;
            }
            
            // Add contract analysis link if creating from contract
            if (analysisId && !editingVariant) {
                submitData.contract_analysis = analysisId;
                submitData.has_contract = true;
            }
            
            // Remove empty fields, but preserve spec_attributes (object/null are intentional payload values).
            Object.keys(submitData).forEach(key => {
                if (key === 'spec_attributes') return;
                if (!submitData[key] || submitData[key].toString().trim() === '') {
                    delete submitData[key];
                }
            });
            
            let response;
            if (editingVariant) {
                console.log('Updating stock variant with data:', submitData);
                response = await axiosInstance.patch(`/api/stock/stock-variants/${editingVariant.id}/`, submitData);
                console.log('Stock variant updated successfully:', response.data);
            } else if (pendingStockItem) {
                console.log('Creating product + first item atomically:', pendingStockItem, submitData);
                const created = await axiosInstance.post('/api/stock/stock-items/', {
                    ...pendingStockItem,
                    initial_variant: submitData,
                });
                // Callers expect the ITEM back (the product is addressable from it).
                response = { data: created.data.initial_variant };
                if (created.data.is_existing) {
                    showSuccess(created.data.message || 'Product already exists — the item was added to it.');
                }
                console.log('Product + item created successfully:', created.data);
            } else {
                console.log('Creating stock variant with data:', submitData);
                response = await axiosInstance.post('/api/stock/stock-variants/', submitData);
                console.log('Stock variant created successfully:', response.data);
            }
            
            // Sprint D: emit the new/updated variant so callers (e.g. InlineProductCreator)
            // can auto-attach it to the line item that triggered the flow. The pre-existing
            // StockModule.handleVariantCreated() caller declares no parameters and ignores
            // the argument, so this remains backwards-compatible.
            onSuccess(response.data);
            // Don't call handleClose() on successful submission - let parent handle it
        } catch (error) {
            console.error('Error creating stock variant:', error);
            // In pending mode one POST carries both halves, so a 400 can name
            // ITEM fields (nested under initial_variant) or PRODUCT fields.
            // Show each on the right form instead of dropping either silently.
            if (pendingStockItem && error.response?.status === 400) {
                const data = error.response.data || {};
                const { initial_variant: itemErrors, ...productErrors } = data;
                const productMessages = Object.entries(productErrors)
                    .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(' ') : val}`);
                setErrors({
                    ...(itemErrors || {}),
                    ...(productMessages.length
                        ? { general: `Product details rejected — ${productMessages.join(' · ')}` }
                        : {}),
                });
                setLoading(false);
                return;
            }
            
            // Handle different error types
            if (error.response?.status === 400) {
                // Validation errors
                const errorData = error.response.data;
                console.error('Validation errors:', errorData);
                setErrors(errorData);
            } else if (error.response?.status === 401) {
                setErrors({ general: 'You are not authorized to create stock variants. Please log in again.' });
            } else if (error.response?.status === 404) {
                setErrors({ general: 'Stock API endpoint not found. Please contact support.' });
            } else if (error.response?.status >= 500) {
                setErrors({ general: 'Server error. Please try again later.' });
            } else {
                setErrors({ general: 'Failed to create stock variant. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = async () => {
        // If we have an analysis ID and this variant was created from a contract, discard it since user is canceling
        if (analysisId && preFilledData) {
            try {
                console.log('🗑️ Auto-discarding contract analysis on variant form close:', analysisId);
                showSuccess('Discarding contract analysis...');
                await axiosInstance.delete(`/api/stock/contracts/discard/${analysisId}/`);
                console.log('✅ Contract analysis auto-discarded on variant form close');
                showSuccess('Contract analysis discarded successfully');
            } catch (error) {
                console.error('❌ Failed to auto-discard contract analysis:', error);
                // Handle different error types
                if (error.response?.status === 404) {
                    console.log('ℹ️ Analysis already deleted or not found');
                    // Continue with close anyway
                } else {
                    showError('Failed to discard contract analysis. Please try again.');
                    return; // Don't close modal if discard failed
                }
            }
        }
        
        setFormData({
            shortname: '',
            quality: '',
            quantity: '',
            size: '',
            purchase_price: '',
            sale_price: '',
            spec_attributes: { ...EMPTY_MIXED_FUELS_SPEC },
            source_type: 'manual'
        });
        setErrors({});
        setShortnameTouched(false);
        onClose();
    };

    if (!isOpen) {
        return null;
    }

    const modalContent = (
        <div 
            className="modal-overlay" 
            style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10000, 
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={handleClose}
        >
            <div 
                className="modal-content variant-form-modal" 
                style={{ 
                    zIndex: 10001,
                    backgroundColor: 'white',
                    padding: 0,
                    borderRadius: '0.75rem',
                    maxWidth: '600px',
                    width: '90vw',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="modal-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1.25rem 1.5rem',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    <h2 style={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: '#1e293b'
                    }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2.25rem',
                            height: '2.25rem',
                            marginRight: '0.75rem',
                            borderRadius: '0.5rem',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: '#ffffff',
                            fontSize: '1rem',
                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.25)',
                            flexShrink: 0
                        }}>
                            <FontAwesomeIcon icon={faBoxes} />
                        </span>
                        {editingVariant ? 'Edit Product Variant' : 'Add Product Variant'}
                    </h2>
                    <button 
                        type="button"
                        className="modal-close-btn" 
                        onClick={handleClose}
                        disabled={loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2rem',
                            height: '2rem',
                            border: 'none',
                            background: 'none',
                            fontSize: '1.25rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            color: '#64748b',
                            borderRadius: '0.375rem',
                            padding: 0
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="variant-form" style={{ padding: '1.5rem' }}>
                                        {errors.general && (
                        <div className="error-banner" style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                            {errors.general}
                        </div>
                    )}
                    
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label htmlFor="shortname" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Shortname *</label>
                        <input
                            type="text"
                            id="shortname"
                            name="shortname"
                            value={formData.shortname}
                            onChange={handleChange}
                            placeholder={showMixedFuelsSpec
                                ? 'Auto-filled from the spec below, or type your own'
                                : 'Enter a short name (max 64 characters)'}
                            className={errors.shortname ? 'error' : ''}
                            disabled={loading}
                            maxLength={SHORTNAME_MAX_LENGTH}
                            style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.95rem' }}
                        />
                        {errors.shortname && (
                            <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.shortname}</span>
                        )}
                    </div>

                    {showMixedFuelsSpec && (
                        <div
                            className="mixed-fuels-spec-section"
                            style={{
                                marginBottom: '1rem',
                                padding: '1rem',
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem',
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    fontSize: '0.9rem',
                                }}
                            >
                                <FontAwesomeIcon icon={faGasPump} style={{ color: '#3b82f6' }} />
                                Mixed Fuels Specification
                            </div>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                    gap: '0.75rem',
                                }}
                            >
                                {MIXED_FUELS_FIELDS.map((field) => (
                                    <div key={field.key} className="form-group" style={{ minWidth: 0 }}>
                                        <label
                                            htmlFor={`spec-${field.key}`}
                                            style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.85rem' }}
                                        >
                                            {field.label}
                                        </label>
                                        <select
                                            id={`spec-${field.key}`}
                                            value={formData.spec_attributes?.[field.key] || ''}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    spec_attributes: {
                                                        ...(prev.spec_attributes || {}),
                                                        [field.key]: e.target.value,
                                                    },
                                                }))
                                            }
                                            disabled={loading}
                                            style={{
                                                width: '100%',
                                                padding: '0.65rem 0.75rem',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                            }}
                                        >
                                            <option value="">{field.placeholder}</option>
                                            {field.options.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            <div
                                style={{
                                    marginTop: '0.75rem',
                                    padding: '0.5rem 0.75rem',
                                    background: liveShorthand ? '#eff6ff' : '#f1f5f9',
                                    border: `1px dashed ${liveShorthand ? '#93c5fd' : '#cbd5e1'}`,
                                    borderRadius: '0.375rem',
                                    fontSize: '0.8rem',
                                    color: liveShorthand ? '#1e40af' : '#64748b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span style={{ fontWeight: 600 }}>Shorthand:</span>
                                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                    {liveShorthand || 'Pick all four fields to generate a shorthand'}
                                </span>
                            </div>
                            <small
                                style={{
                                    display: 'block',
                                    marginTop: '0.5rem',
                                    color: '#6b7280',
                                    fontSize: '0.75rem',
                                }}
                            >
                                All four spec fields are optional. When all four are set, the shorthand auto-fills the shortname above (until you edit it).
                            </small>
                        </div>
                    )}

                    <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="unit" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Unit of Measurement</label>
                            <select
                                id="unit"
                                name="unit"
                                value={formData.unit || ''}
                                onChange={handleChange}
                                className={errors.unit ? 'error' : ''}
                                disabled={loading}
                                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.95rem' }}
                            >
                                <option value="">Select unit...</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>
                                        {unit.name}
                                    </option>
                                ))}
                            </select>
                            {errors.unit && (
                                <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.unit}</span>
                            )}
                        </div>
                        
                        <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="quantity" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                                Opening stock ({getUnitLabel()})
                            </label>
                            <AmountInput
                                id="quantity"
                                name="quantity"
                                value={formData.quantity}
                                onChange={(v) => setAmountField('quantity', v)}
                                placeholder={editingVariant && editingVariant.has_movements ? 'Locked — manage stock from the Stockpile panel' : `Enter on-hand quantity in ${getUnitLabel()}`}
                                className={errors.quantity ? 'error' : ''}
                                disabled={loading || (editingVariant && editingVariant.has_movements)}
                            />
                            <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.4 }}>
                                {editingVariant && editingVariant.has_movements
                                    ? 'Locked because this variant already has stockpile movements. Post inbound, outbound, or adjustment movements from the Stockpile panel on the variant card.'
                                    : 'How much you have on hand right now. Leave at 0 if goods are arriving via a delivery — inbound stock will auto-post when the delivery\u2019s Bill of Lading is finalised.'}
                            </div>
                            {errors.quantity && (
                                <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.quantity}</span>
                            )}
                        </div>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label htmlFor="size" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Size Specifications</label>
                        <input
                            type="text"
                            id="size"
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            placeholder="Enter size specifications"
                            className={errors.size ? 'error' : ''}
                            disabled={loading}
                            style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.95rem' }}
                        />
                        {errors.size && (
                            <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.size}</span>
                        )}
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label htmlFor="price_currency" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>Price Currency *</label>
                        <div style={{ position: 'relative' }}>
                            <select
                                id="price_currency"
                                name="price_currency"
                                value={formData.price_currency}
                                onChange={handleChange}
                                className={errors.price_currency ? 'error' : ''}
                                disabled={loading}
                                style={{ 
                                    width: '100%', 
                                    padding: '0.75rem 2.5rem 0.75rem 0.75rem', 
                                    border: errors.price_currency ? '2px solid #ef4444' : '1px solid #d1d5db', 
                                    borderRadius: '0.375rem', 
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    backgroundColor: loading ? '#f9fafb' : 'white',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 0.75rem center',
                                    backgroundSize: '1rem',
                                    transition: 'all 0.2s ease-in-out',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#3b82f6';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = errors.price_currency ? '#ef4444' : '#d1d5db';
                                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.borderColor = '#9ca3af';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!e.target.matches(':focus')) {
                                        e.target.style.borderColor = errors.price_currency ? '#ef4444' : '#d1d5db';
                                    }
                                }}
                            >
                                {CURRENCY_OPTIONS.map(option => (
                                    <option 
                                        key={option.value} 
                                        value={option.value}
                                        style={{
                                            padding: '0.5rem',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            color: '#374151'
                                        }}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.price_currency && (
                            <span className="error-text" style={{ 
                                color: '#ef4444', 
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginTop: '0.25rem',
                                display: 'block'
                            }}>
                                {errors.price_currency}
                            </span>
                        )}
                    </div>
                    
                    <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="purchase_price" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Purchase Price (per {getUnitLabel()})</label>
                            <AmountInput
                                id="purchase_price"
                                name="purchase_price"
                                value={formData.purchase_price}
                                onChange={(v) => setAmountField('purchase_price', v)}
                                placeholder={`Enter purchase price per ${getUnitLabel()}`}
                                className={errors.purchase_price ? 'error' : ''}
                                disabled={loading}
                            />
                            {errors.purchase_price && (
                                <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.purchase_price}</span>
                            )}
                        </div>
                        
                        <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="sale_price" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Sale Price (per {getUnitLabel()})</label>
                            <AmountInput
                                id="sale_price"
                                name="sale_price"
                                value={formData.sale_price}
                                onChange={(v) => setAmountField('sale_price', v)}
                                placeholder={`Enter sale price per ${getUnitLabel()}`}
                                className={errors.sale_price ? 'error' : ''}
                                disabled={loading}
                            />
                            {errors.sale_price && (
                                <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.sale_price}</span>
                            )}
                        </div>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label htmlFor="quality" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Quality Specifications</label>
                        <textarea
                            id="quality"
                            name="quality"
                            value={formData.quality}
                            onChange={handleChange}
                            placeholder="Enter detailed quality specifications, certifications, analysis results, or any other quality-related information..."
                            className={errors.quality ? 'error' : ''}
                            disabled={loading}
                            rows={4}
                            style={{ 
                                width: '100%', 
                                padding: '0.75rem', 
                                border: '1px solid #d1d5db', 
                                borderRadius: '0.375rem', 
                                fontSize: '0.95rem',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                lineHeight: '1.5'
                            }}
                        />
                        {errors.quality && (
                            <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.quality}</span>
                        )}
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Shipping Tolerance (Optional)
                        </label>
                        <div className="tolerance-inputs-container" style={{ display: 'flex', gap: '15px', width: '100%' }}>
                            <div className="tolerance-input-group" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <div className="tolerance-input-with-dropdown" style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        name="tolerance_lower_value"
                                        value={formData.tolerance_lower_value}
                                        onChange={handleChange}
                                        placeholder="0"
                                        inputMode="decimal"
                                        disabled={loading}
                                        style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.95rem', minWidth: 0 }}
                                    />
                                    <select
                                        name="tolerance_lower_unit"
                                        value={formData.tolerance_lower_unit}
                                        onChange={handleChange}
                                        disabled={loading}
                                        style={{ 
                                            width: '70px', 
                                            padding: '0.75rem 0.5rem', 
                                            border: '1px solid #d1d5db', 
                                            borderRadius: '0.375rem',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="%">%</option>
                                        <option value="min">min</option>
                                    </select>
                                </div>
                                <small className="field-description" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Lower</small>
                            </div>
                            
                            <div className="tolerance-input-group" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <div className="tolerance-input-with-dropdown" style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        name="tolerance_upper_value"
                                        value={formData.tolerance_upper_value}
                                        onChange={handleChange}
                                        placeholder="0"
                                        inputMode="decimal"
                                        disabled={loading}
                                        style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.95rem', minWidth: 0 }}
                                    />
                                    <select
                                        name="tolerance_upper_unit"
                                        value={formData.tolerance_upper_unit}
                                        onChange={handleChange}
                                        disabled={loading}
                                        style={{ 
                                            width: '70px', 
                                            padding: '0.75rem 0.5rem', 
                                            border: '1px solid #d1d5db', 
                                            borderRadius: '0.375rem',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="%">%</option>
                                        <option value="max">max</option>
                                    </select>
                                </div>
                                <small className="field-description" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Upper</small>
                            </div>
                        </div>
                        <small style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                            Leave empty for products without shipping tolerance requirements (e.g., barrels, liquid products)
                        </small>
                    </div>
                    
                    <div className="help-text" style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.375rem' }}>
                        <p style={{ margin: '0', fontSize: '0.875rem', color: '#6b7280' }}>Quantity and unit are optional -- leave empty when using products as categories. Fill in other fields as needed.</p>
                    </div>
                    
                    <div className="modal-actions" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={loading}
                            style={{ 
                                padding: '0.75rem 1.5rem', 
                                border: '1px solid #d1d5db', 
                                backgroundColor: '#f9fafb', 
                                color: '#374151',
                                borderRadius: '0.375rem',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                            style={{ 
                                padding: '0.75rem 1.5rem', 
                                backgroundColor: '#3b82f6', 
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            {loading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    {editingVariant ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    {editingVariant ? 'Update Variant' : 'Add Variant'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    // Render modal using Portal to document.body
    return ReactDOM.createPortal(modalContent, document.body);
};

export default StockItemVariantForm; 